
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Center {
  id: string; // TEXT center_id (e.g., "caroline-springs")
  uuid: string; // UUID from database id field for pricing queries
  name: string;
  suburb: string;
  state: string;
  stateCode: string;
  address: string;
  phone: string;
  email: string;
  slug: string;
  isOpen: boolean;
  comingSoon?: boolean;
  franchiseCalendarId?: string;
  franchiseEmail?: string;
  timeZone?: string;
  heroImage?: string;
}

interface StateConfig {
  code: string;
  name: string;
  centers: Center[];
  heroImage?: string;
}

interface LocationContextType {
  selectedState: string;
  setSelectedState: (state: string) => void;
  selectedCenter: string | null;
  setSelectedCenter: (center: string | null) => void;
  selectedCenterId: string | null;
  setSelectedCenterId: (centerId: string | null) => void;
  currentStateConfig: StateConfig | null;
  stateConfigs: StateConfig[];
  loading: boolean;
  refreshCenters: () => Promise<void>;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

// Helper function to transform database data to Center interface
const transformDbCenter = (dbCenter: any): Center => ({
  id: dbCenter.center_id,
  uuid: dbCenter.id, // Store the actual UUID for pricing queries
  name: dbCenter.name,
  suburb: dbCenter.suburb,
  state: dbCenter.state_name,
  stateCode: dbCenter.state_code,
  address: dbCenter.address,
  phone: dbCenter.phone,
  email: dbCenter.email,
  slug: dbCenter.slug,
  isOpen: dbCenter.is_open,
  comingSoon: dbCenter.coming_soon,
  franchiseCalendarId: dbCenter.franchise_calendar_id || dbCenter.email, // Use email as fallback
  franchiseEmail: dbCenter.email,
  timeZone: dbCenter.time_zone,
  heroImage: dbCenter.hero_image
});

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize state from localStorage or default to NSW
  const [selectedState, setSelectedState] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('schoolola_selected_state') || 'NSW';
    }
    return 'NSW';
  });
  
  const [selectedCenter, setSelectedCenter] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('schoolola_selected_center') || null;
    }
    return null;
  });
  
  const [selectedCenterId, setSelectedCenterId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('schoolola_selected_center_id') || null;
    }
    return null;
  });

  const [stateConfigs, setStateConfigs] = useState<Record<string, StateConfig>>({});
  const [loading, setLoading] = useState(true);

  // Fetch centers from database
  const fetchCenters = async () => {
    try {
      setLoading(true);
      const { data: centers, error } = await supabase
        .from('schoolola_centers')
        .select('*')
        .order('state_code', { ascending: true })
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching centers:', error);
        return;
      }

      // Show centres that are operational (is_open=true) or pre-launch (coming_soon=true).
      // Closed centres (both flags false) are hidden automatically based on DB state.
      const visibleCenters = centers?.filter(center => center.is_open || center.coming_soon) || [];


      // Group centers by state
      const stateGroups = visibleCenters.reduce((acc, center) => {
        const stateCode = center.state_code;
        if (!acc[stateCode]) {
          acc[stateCode] = {
            code: stateCode,
            name: center.state_name,
            centers: [],
            heroImage: center.hero_image,
            defaultCalendlyUrl: center.default_calendly_url
          };
        }
        acc[stateCode].centers.push(transformDbCenter(center));
        return acc;
      }, {} as Record<string, StateConfig>);

      // Add empty states for states without centers
      const allStates = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
      const stateNames = {
        NSW: 'New South Wales',
        VIC: 'Victoria', 
        QLD: 'Queensland',
        SA: 'South Australia',
        WA: 'Western Australia',
        TAS: 'Tasmania',
        NT: 'Northern Territory',
        ACT: 'Australian Capital Territory'
      };

      allStates.forEach(stateCode => {
        if (!stateGroups[stateCode]) {
          stateGroups[stateCode] = {
            code: stateCode,
            name: stateNames[stateCode as keyof typeof stateNames],
            centers: []
          };
        }
      });

      setStateConfigs(stateGroups);
    } catch (error) {
      console.error('Error fetching centers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchCenters();
  }, []);

  // Save state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('schoolola_selected_state', selectedState);
  }, [selectedState]);

  useEffect(() => {
    if (selectedCenter) {
      localStorage.setItem('schoolola_selected_center', selectedCenter);
    }
  }, [selectedCenter]);

  useEffect(() => {
    if (selectedCenterId) {
      localStorage.setItem('schoolola_selected_center_id', selectedCenterId);
    }
  }, [selectedCenterId]);

  const currentStateConfig = stateConfigs[selectedState] || null;

  return (
    <LocationContext.Provider value={{
      selectedState,
      setSelectedState,
      selectedCenter,
      setSelectedCenter,
      selectedCenterId,
      setSelectedCenterId,
      currentStateConfig,
      stateConfigs: Object.values(stateConfigs),
      loading,
      refreshCenters: fetchCenters
    }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    // Return a default context to prevent errors during initialization
    return {
      selectedState: 'NSW',
      setSelectedState: () => {},
      selectedCenter: null,
      setSelectedCenter: () => {},
      selectedCenterId: null,
      setSelectedCenterId: () => {},
      currentStateConfig: {
        code: 'NSW',
        name: 'New South Wales', 
        heroImage: '',
        defaultCalendlyUrl: '',
        centers: []
      },
      stateConfigs: [],
      loading: true,
      refreshCenters: async () => {}
    };
  }
  return context;
};
